import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, AuthErrorHandler } from "@monorepo/auth-utils";
import { ToastProvider, ToastContainer } from "@monorepo/ui";
import { ROUTES } from "@monorepo/config";
import { DevLinks } from "./components/DevTools/DevLinks";
import "./styles/loader.scss";

// ----------------------------------------------------------------------
// Lazy Imports
// ----------------------------------------------------------------------

const SigninPage = lazy(() =>
  import("./pages/SigninPage/SigninPage").then((m) => ({
    default: m.SigninPage,
  }))
);

// Standard MFA Verification Pages
const MfaMethodPage = lazy(() =>
  import("./pages/MfaMethodPage/MfaMethodPage").then((m) => ({
    default: m.MfaMethodPage,
  }))
);
const MfaTotpPage = lazy(() =>
  import("./pages/MfaTotpPage/MfaTotpPage").then((m) => ({
    default: m.MfaTotpPage,
  }))
);
const MfaBackupCodePage = lazy(() =>
  import("./pages/MfaBackupCodePage/MfaBackupCodePage").then((m) => ({
    default: m.MfaBackupCodePage,
  }))
);

// Employee Onboarding Pages (New)
const ForceChangePasswordPage = lazy(() =>
  import("./pages/ForceChangePasswordPage/ForceChangePasswordPage").then(
    (m) => ({
      default: m.ForceChangePasswordPage,
    })
  )
);

const MfaTotpSetupPage = lazy(() =>
  import("./pages/MfaTotpSetupPage/MfaTotpSetupPage").then((m) => ({
    default: m.MfaTotpSetupPage,
  }))
);

const MfaBackupSetupPage = lazy(() =>
  import("./pages/MfaBackupSetupPage/MfaBackupSetupPage").then((m) => ({
    default: m.MfaBackupSetupPage,
  }))
);

// Common Pages
const SuccessPage = lazy(() =>
  import("./pages/SuccessPage/SuccessPage").then((m) => ({
    default: m.SuccessPage,
  }))
);
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage/NotFoundPage").then((m) => ({
    default: m.NotFoundPage,
  }))
);

// ----------------------------------------------------------------------
// Components
// ----------------------------------------------------------------------

const PageLoader = () => (
  <div className="loading-container">
    <div className="spinner"></div>
    <span className="loading-text">Loading Sign In...</span>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AuthErrorHandler />
        <BrowserRouter
          basename="/signin"
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* 1. Main Signin Flow (Merged Login) */}
              <Route path={ROUTES.SIGNIN} element={<SigninPage />} />

              {/* 2. Employee Onboarding Flow (Sequence) */}
              <Route
                path={ROUTES.SIGNIN_FORCE_CHANGE_PASSWORD}
                element={<ForceChangePasswordPage />}
              />
              <Route
                path={ROUTES.SIGNIN_MFA_SETUP}
                element={<MfaTotpSetupPage />}
              />
              <Route
                path={ROUTES.SIGNIN_MFA_BACKUP_SETUP}
                element={<MfaBackupSetupPage />}
              />

              {/* 3. MFA Verification Flow (Standard Login) */}
              <Route path={ROUTES.SIGNIN_MFA} element={<MfaMethodPage />} />
              <Route path={ROUTES.SIGNIN_MFA_TOTP} element={<MfaTotpPage />} />
              <Route
                path={ROUTES.SIGNIN_MFA_BACKUP}
                element={<MfaBackupCodePage />}
              />

              {/* 4. End State */}
              <Route path={ROUTES.SIGNIN_SUCCESS} element={<SuccessPage />} />

              {/* 404 fallback */}
              <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
              <Route path="*" element={<Navigate to={ROUTES.NOT_FOUND} replace />} />
            </Routes>
          </Suspense>

          {/* Global Toast */}
          <ToastContainer />

          {/* Dev Tools - Securely guarded for Development only */}
          {import.meta.env.DEV && <DevLinks />}
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
